import {useNavigate} from 'react-router'
import {useTranslation} from 'react-i18next'
import {useApolloClient, useMutation} from '@apollo/client'
import {
  getProcessName,
  getTaskCreatedAt,
  getSubject
} from 'components/form/utils/form-utils'
import {useResponsiveColumns} from 'hooks/columns'
import LargeElevatedTable from 'components/tables/LargeElevatedTable'
import {GET_ASSIGNED_TASKS, HIDE_TASK} from 'queries/task'
import {useTranslator} from 'hooks/translator'
import {getTaskTitle} from 'contexts/FormInfoContext'
import {openTaskForm} from 'hooks/process'
import {useNotifier} from 'hooks/notification'
import {createDataLoader} from 'utils/table'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import React from 'react'
import { IconButton, Tooltip } from '@mui/material'
import { useToolbar } from 'contexts/ToolbarContext'
import Report from 'helpers/report'

const AssignedTasksTable = () => {
  const [showHidden, setShowHidden] = React.useState(false)

  const client = useApolloClient()
  const navigate = useNavigate()
  const columns = useColumns()
  const {translator, t} = useTranslator()
  const notifier = useNotifier()
  const options = {translator, navigate, notifier, register: true}

  const requestInfo = {
    variables: {priority: showHidden ? -2 : -1},
    query:     GET_ASSIGNED_TASKS,
    dataPath:  'data.assignedTasks',
    countPath: 'data.assignedTasksCount',
  }

  const toTableRow = (row) => toTableRowData(options, row)
  const dataLoader = createDataLoader(client, requestInfo, toTableRow)

  const ToggleHiddenButton = () => {
    const { reload } = useToolbar()

    return <Tooltip title={t('tasks.showHidden')}>
      <IconButton onClick={_e => {setShowHidden(!showHidden); reload()}}>
        {showHidden ? <Visibility/> : <VisibilityOff/>}
      </IconButton>
    </Tooltip>
  }

  return <LargeElevatedTable
    description="assigned tasks"
    filterType="local"
    columns={columns}
    dataLoader={dataLoader}
    rowAction={hideTaskAction}
    extraMenuItems={() => [<ToggleHiddenButton key='toggleHiddenTasks'/>]}
  />
}

const hideTaskAction = (task) => {
  const { reload }        = useToolbar()
  const [hideTask]        = useMutation(HIDE_TASK)
  const { translator, t } = useTranslator()
  const notifier          = useNotifier()

  const handleClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    hideTask({ variables: {id: task.id} })
      .then(
        _result => {
          reload()
        },
        error  => {
          const report = Report.from(error, translator, { category: Report.backend })
          report.addToNotifier(notifier)
          console.error("Hide task error: %s", report.verboseMessage)
        }
      )
  }

  // tasks with priority -1 or lower are considered hidden
  return task.priority < 0
    ? <IconButton disabled size='small'><VisibilityOff color='disabled'/></IconButton>
    : <Tooltip title={t('tasks.hideTask')}>
    <IconButton size='small' onClick={handleClick}>
      <VisibilityOff/>
    </IconButton>
  </Tooltip>
}

function toTableRowData(options, task) {
  const { translator } = options
  const t = translator.translate.bind(translator)

  return {
    id: task.id,
    priority: task.priority,
    onClick: (e) => openTaskForm(options, e, task), 
    cells: {
      'processName' :    task.processDefinition ? getProcessName(translator, task) : t('unknown'),
      //'taskDescription': getTaskDescription(translator, task),
      'taskTitle':       getTaskTitle(translator, {isStartForm: false, task}), 
      'taskSubject':     getSubject(task),
      'assignee':        task.assignee && task.assignee.username,
      'createdAt':       getTaskCreatedAt(task)
    }
  }
}

const useColumns = () => {
  const { t } = useTranslation()

  const processName = {
    headerName: t('process_name'),
    field: "processName",
    cellProps: {
      width: 200,
      align: "left"
    }
  }

  const subject = {
    headerName: t('subject'),
    field: "taskSubject",
    cellProps: {
      width: 200,
      align: "left"
    }
  }  

  const process_step = {
    headerName: t('menu.process_step'),
    field: "taskTitle",
    cellProps: {
      width: 200,
      align: "left"
    }
  }

  const description = {
    headerName: t('description'),
    field: "taskDescription",
    cellProps: {
      width: 350,
      align: "left"
    }
  }

  const createdAt = {
    headerName: t('createdAt'),
    field: "createdAt",
    cellProps: {
      width: 100,
      align: "left",
      sx: {whiteSpace: "nowrap"}
    }
  }

  const columns = {
    xxs: [subject],
    md: [
      processName,
      subject 
    ],
    lg: [
      processName,
      subject,
      process_step,
      //description
    ],
    xl: [
      processName,
      subject,
      process_step,
      //description,
      createdAt,
    ]
  }

  return useResponsiveColumns(columns, true)
}

export default AssignedTasksTable;
